import React from "react";
import { CV } from "../components/cv";
import JerryHImg from "@/assets/images/jerry-h.png";

export default function CVJerryHon() {
  React.useEffect(() => {
    document.title = "BoldBrave CV - Jerry Hon";
  }, []);

  return (
    <CV
      name="Jerry Hon"
      photo={JerryHImg}
      title="Senior Frontend Developer · Senior Fullstack Developer"
      subtitle="15+ years in web development. React, Vue and TypeScript on the frontend, C# and Node.js on the backend, across e-commerce, pharma and automotive."
      summary="Senior frontend and fullstack developer with more than 15 years of experience building customer-facing web platforms and internal applications. Has led frontend development for e-commerce brands such as Dometic, Kicks, Filippa K and Polarn O. Pyret, built the new frontend foundation for the Porsche website, and delivered end-to-end applications in Azure for Novo Nordisk. Comfortable across the stack, from React and Vue components to C# integrations, CI/CD pipelines and cloud setup. Experienced technical lead who has driven architecture decisions, introduced testing practices and led frontend teams of up to 12 developers."
      skills={{
        Frontend: [
          "JavaScript",
          "TypeScript",
          "React",
          "Redux",
          "Vue",
          "Astro",
          "Angular",
          "AngularJS",
          "Vite",
          "CSS",
          "Sass",
          "CSS-in-JS",
        ],
        Backend: ["Node.js", "C#", "Java", "PHP", "MongoDB", "MySQL"],
        "CMS & Commerce": ["Optimizely / Episerver", "Omnium", "Strapi", "Drupal"],
        "Cloud & DevOps": [
          "Azure",
          "Azure Service Bus",
          "AWS",
          "GCP",
          "Docker",
          "GitHub Actions",
          "GitLab CI",
          "TeamCity",
          "Octopus",
        ],
        Testing: ["Playwright", "Unit testing"],
        "AI Tools": ["Claude"],
        Languages: [
          "Swedish (native)",
          "Mandarin (native)",
          "Cantonese (native)",
          "English (fluent)",
        ],
      }}
      experience={[
        {
          role: "Senior Fullstack Developer",
          company: "Avensia",
          period: "Oct 2025 – Sep 2026",
          description:
            "Senior fullstack developer on a key client project, implementing customer-facing web features and system integrations.",
          highlights: [
            "Implemented workflows against a new OMS (Omnium) using C# and Azure Service Bus.",
            "Built responsive frontend components in React.",
            "Worked within an Azure, Optimizely and GitHub ecosystem.",
          ],
        },
        {
          role: "Senior Frontend Developer",
          company: "Novo Nordisk",
          period: "Feb 2025 – Aug 2025",
          description:
            "Developed and maintained an internal web application for a product team, end to end.",
          highlights: [
            "Set up both frontend and backend applications in Azure.",
            "Built with Vue, TypeScript, Vite and Node.js.",
            "Implemented CI/CD pipelines with GitHub Actions.",
            "Used Strapi as headless CMS and Playwright for end-to-end testing.",
          ],
        },
        {
          role: "Senior Frontend Developer",
          company: "MHP (client: Porsche)",
          period: "May 2023 – Dec 2024",
          description:
            "Built the new frontend tech stack for the Porsche website.",
          highlights: [
            "Delivered fast, scalable experiences with Astro, Vue, TypeScript and Vite.",
            "Developed core functionality and shared frontend foundation packages used across multiple teams.",
            "Worked with Sass, Node.js tooling, GitLab pipelines, AWS and Playwright.",
          ],
        },
        {
          role: "Senior System Developer",
          company: "Afound",
          period: "Oct 2022 – May 2023",
          description:
            "Frontend-focused development of new features and optimisation of existing frontend bundles.",
          highlights: [
            "Improved performance and maintainability of frontend bundles.",
            "Active in technical decisions shaping the frontend tech stack.",
            "Stack: React, TypeScript, Sass and Node.js in Azure, with C# backend services and Optimizely.",
          ],
        },
        {
          role: "Senior Frontend Developer",
          company: "Normative",
          period: "Feb 2022 – Jul 2022",
          description:
            "Delivered new features and maintained existing applications.",
          highlights: [
            "Helped set up a new tech stack moving towards a micro frontend architecture with React.",
            "Stack: React, Angular, TypeScript, Sass, Node.js, MongoDB, AWS.",
          ],
        },
        {
          role: "Lead Frontend Developer",
          company: "Avensia (client: Dometic US)",
          period: "Sep 2020 – Jan 2022",
          description:
            "Led frontend development for the new Dometic US site and continuous rollout of new markets.",
          highlights: [
            "Developed and launched the new Dometic US e-commerce site.",
            "Set up CI environment and deployment steps (TeamCity, Octopus).",
            "Stack: React, Redux, TypeScript, CSS-in-JS, Node.js, Episerver, C#.",
          ],
        },
        {
          role: "Lead Frontend Developer",
          company: "Diaverum",
          period: "Mar 2020 – Sep 2020",
          description:
            "Lead frontend developer in the core team, setting up the structure for a new frontend application and platform.",
          highlights: [
            "Upgraded the existing component library.",
            "Led the migration to CSS-in-JS for better code usability and quality.",
          ],
        },
        {
          role: "Lead Frontend Developer",
          company: "IKEA",
          period: "May 2019 – Mar 2020",
          description:
            "Lead frontend developer in one of the customer self-service teams.",
          highlights: [
            "Maintained legacy applications and built new applications and features, including a Firebase-based configuration app.",
            "Led the migration to a new CI/CD flow in GCP.",
            "Introduced unit testing and best practices.",
          ],
        },
        {
          role: "Senior System Developer",
          company: "Avensia",
          period: "Aug 2016 – May 2019",
          description:
            "Led frontend development and UX discussions for e-commerce platforms.",
          highlights: [
            "Delivered new e-commerce platforms for Kicks, Filippa K and Polarn O. Pyret.",
            "Stack: React, Redux, TypeScript, CSS-in-JS, Sass, Episerver, C#.",
          ],
        },
        {
          role: "Senior Software Engineer",
          company: "Capgemini",
          period: "Mar 2016 – Jun 2016",
          description:
            "Led frontend development and set up a new frontend architecture for an existing product (AngularJS, Episerver, C#).",
        },
        {
          role: "Technical Lead / Frontend Developer",
          company: "Mobenga",
          period: "Jan 2014 – Mar 2016",
          description:
            "Drove technical solution and architecture for a new mobile and desktop betting platform for one of the UK's largest online bookmakers.",
          highlights: [
            "Built a scalable platform designed for high traffic.",
            "Led frontend development across 3 sub-teams with 12 frontend developers.",
          ],
        },
        {
          role: "System Developer",
          company: "Axis Communications",
          period: "Jan 2011 – Jan 2014",
          description:
            "Developed internal applications such as a CRM system, newsletter tool and video management tool, working in Scrum and Kanban.",
        },
        {
          role: "IT Consultant",
          company: "Örestad Linux",
          period: "Apr 2009 – Nov 2010",
          description:
            "Drupal and PHP development, including a modular training application.",
        },
      ]}
      education={[
        {
          degree: "Bachelor of Graphic Technology and Media",
          institution: "Malmö University",
          year: "2005 – 2008",
        },
        {
          degree: "Courses in Computer Security and C# Programming",
          institution: "Gotland University",
          year: "2010",
        },
        {
          degree: "Agile Methods (Scrum)",
          institution: "Softhouse",
          year: "2013",
        },
      ]}
      certifications={[
        { name: "Episerver CMS 10 Certified Developer", year: "2018" },
      ]}
    />
  );
}
